import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import {
  Search, X, Plus, Download, Phone, MessageCircle, Users, Sparkles, AlarmClock, TrendingUp,
  ChevronLeft, ChevronRight, ArrowUpDown, CalendarPlus, Mail, MapPin, Megaphone,
} from "lucide-react";
import SegmentedControl from "../../components/SegmentedControl";
import FilterMultiSelect from "../../components/FilterMultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { downloadXlsx } from "../../lib/xlsx";
import { cn } from "../../lib/utils";
import {
  generateDemoLeads, LEAD_SOURCES, SOURCE_BY_KEY, LEAD_STATUSES, STATUS_BY_KEY,
  LEAD_SERVICES, LOST_REASONS,
} from "../../lib/demo-leads";

// Demo-only (see navigations/main.js): a front-end mock of a leads inbox fed
// by generated data, to show clinics what MediPro's lead management would
// look like. Nothing here is saved — a reload starts over.

const PAGE_SIZE = 25;
const OPEN = ["new", "contacted", "follow_up"];

const ENQUIRY_PRESETS = [
  { value: "any", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7d" },
  { value: "30d", label: "Last 30d" },
];
const FOLLOW_UP_OPTIONS = [
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Due today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "week", label: "Next 7 days" },
  { value: "none", label: "No follow-up set" },
];

const selectClass = "h-9 rounded-md border border-input bg-card px-2.5 text-sm";

function StatusBadge({ status }) {
  const s = STATUS_BY_KEY[status];
  if (status === "converted") {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">{s.label}</Badge>;
  }
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

function SourceMark({ source, size = "sm" }) {
  const s = SOURCE_BY_KEY[source];
  return (
    <span
      title={s.label}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md font-bold text-white",
        size === "sm" ? "h-5 min-w-5 px-1 text-[10px]" : "h-8 min-w-8 px-1.5 text-xs"
      )}
      style={{ backgroundColor: s.color }}
    >
      {s.short}
    </span>
  );
}

const followUpState = (lead) => {
  if (!lead.follow_up_at || !OPEN.includes(lead.status)) return null;
  const f = moment(lead.follow_up_at);
  if (f.isBefore(moment(), "day")) return "overdue";
  if (f.isSame(moment(), "day")) return "today";
  return "upcoming";
};

function FollowUpCell({ lead }) {
  const state = followUpState(lead);
  if (!state) return <span className="text-muted-foreground">—</span>;
  const f = moment(lead.follow_up_at);
  return (
    <span
      className={cn(
        "whitespace-nowrap text-sm",
        state === "overdue" && "font-medium text-destructive",
        state === "today" && "font-medium text-warning"
      )}
    >
      {state === "today" ? `Today, ${f.format("h:mm A")}` : f.calendar(null, { sameElse: "D MMM, h:mm A", lastWeek: "D MMM, h:mm A", nextWeek: "ddd, h:mm A" })}
      {state === "overdue" && <span className="block text-xs font-normal">overdue</span>}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        <Icon className={cn("h-4 w-4", tone || "text-muted-foreground")} />
      </div>
      <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const whatsappUrl = (lead) =>
  `https://wa.me/91${lead.phone}?text=${encodeURIComponent(
    `Hello ${lead.name.split(" ")[0]}, thank you for your enquiry about ${lead.service} at Apollo Clinic, Ulubari. When would be a good time to call you?`
  )}`;

// ------------------------------------------------------------------ drawer

function LeadDrawer({ lead, onClose, onUpdate }) {
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [lostReason, setLostReason] = useState(LOST_REASONS[0]);
  const [closing, setClosing] = useState(null); // "lost" | "junk"

  if (!lead) return null;

  const log = (text, patch = {}) =>
    onUpdate(lead.id, {
      ...patch,
      last_note: patch.last_note ?? lead.last_note,
      activity: [{ at: new Date().toISOString(), by: "You", text }, ...lead.activity],
    });

  const setStatus = (status) => {
    if (status === lead.status) return;
    if (status === "lost" || status === "junk") return setClosing(status);
    log(`Status → ${STATUS_BY_KEY[status].label}`, {
      status,
      follow_up_at: OPEN.includes(status) ? lead.follow_up_at : null,
    });
  };

  const addNote = () => {
    if (!note.trim()) return;
    log(note.trim(), { last_note: note.trim() });
    setNote("");
  };

  const bookAppointment = () => {
    navigator.clipboard?.writeText(lead.phone).catch(() => {});
    log("Appointment booked from lead", { status: "booked", follow_up_at: null });
    toast.success(`${lead.phone} copied — paste it on OPD Booking.`);
    navigate("/opd");
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto bg-card shadow-xl">
        <div className="border-b border-border p-5">
          <button onClick={onClose} className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3 pr-6">
            <SourceMark source={lead.source} size="lg" />
            <div className="min-w-0">
              <h3 className="truncate font-serif text-lg font-semibold">{lead.name}</h3>
              <p className="text-sm text-muted-foreground">
                {lead.id} · {SOURCE_BY_KEY[lead.source].label}
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Button size="sm" variant="outline" onClick={() => { window.location.href = `tel:${lead.phone}`; log("Call placed"); }}>
              <Phone className="h-3.5 w-3.5" /> Call
            </Button>
            <Button size="sm" variant="outline" onClick={() => { window.open(whatsappUrl(lead), "_blank"); log("WhatsApp message sent"); }}>
              <MessageCircle className="h-3.5 w-3.5 text-green-600" /> WhatsApp
            </Button>
            <Button size="sm" onClick={bookAppointment}>
              <CalendarPlus className="h-3.5 w-3.5" /> Book
            </Button>
          </div>
        </div>

        <div className="space-y-5 p-5">
          <div className="space-y-2 text-sm">
            <Row icon={Phone} label="Phone" value={lead.phone} />
            {lead.email && <Row icon={Mail} label="Email" value={lead.email} />}
            <Row icon={MapPin} label="City" value={lead.city} />
            <Row icon={Sparkles} label="Interested in" value={lead.service} />
            {lead.campaign && <Row icon={Megaphone} label="Campaign" value={lead.campaign} />}
            <Row icon={AlarmClock} label="Enquired" value={moment(lead.enquiry_at).format("D MMM YYYY, h:mm A")} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <select className={cn(selectClass, "w-full")} value={lead.status} onChange={(e) => setStatus(e.target.value)}>
                {LEAD_STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            {OPEN.includes(lead.status) && (
              <div className="col-span-2 space-y-1.5">
                <Label>Next follow-up</Label>
                <Input
                  type="datetime-local"
                  value={lead.follow_up_at ? moment(lead.follow_up_at).format("YYYY-MM-DDTHH:mm") : ""}
                  onChange={(e) =>
                    e.target.value &&
                    log(`Follow-up set for ${moment(e.target.value).format("D MMM, h:mm A")}`, {
                      follow_up_at: moment(e.target.value).toISOString(),
                    })
                  }
                />
              </div>
            )}
            {lead.lost_reason && ["lost", "junk"].includes(lead.status) && (
              <p className="col-span-2 text-sm text-muted-foreground">Reason: {lead.lost_reason}</p>
            )}
          </div>

          {closing && (
            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
              <Label>Why is this lead {closing === "lost" ? "lost" : "junk"}?</Label>
              <select className={cn(selectClass, "w-full")} value={lostReason} onChange={(e) => setLostReason(e.target.value)}>
                {LOST_REASONS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setClosing(null)}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={() => {
                    log(`Marked ${STATUS_BY_KEY[closing].label} — ${lostReason}`, {
                      status: closing, lost_reason: lostReason, follow_up_at: null,
                    });
                    setClosing(null);
                  }}
                >
                  Confirm
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Add a note</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. Spoke to patient, wants an evening slot"
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={addNote} disabled={!note.trim()}>Save note</Button>
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold">Activity</h4>
            <ol className="relative space-y-4 border-l border-border pl-4">
              {lead.activity.map((a, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-primary" />
                  <p className="text-sm">{a.text}</p>
                  <p className="text-xs text-muted-foreground">
                    {moment(a.at).format("D MMM, h:mm A")} · {a.by}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------- add lead

const EMPTY_LEAD = { name: "", phone: "", source: "phone", service: LEAD_SERVICES[0], follow_up_at: "", note: "" };

function AddLeadModal({ open, onOpenChange, onAdd }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) return toast.error("Enter a valid 10-digit mobile number.");
    onAdd(form);
    setForm(EMPTY_LEAD);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile *</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="9876543210" required />
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <select className={cn(selectClass, "h-10 w-full")} value={form.source} onChange={(e) => set("source", e.target.value)}>
                {LEAD_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Interested in</Label>
              <select className={cn(selectClass, "h-10 w-full")} value={form.service} onChange={(e) => set("service", e.target.value)}>
                {LEAD_SERVICES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Follow-up</Label>
              <Input type="datetime-local" value={form.follow_up_at} onChange={(e) => set("follow_up_at", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="What did they ask about?" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">Add lead</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------- page

export default function Leads() {
  const [leads, setLeads] = useState(() => generateDemoLeads());
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [sources, setSources] = useState([]);
  const [services, setServices] = useState([]);
  const [enquiry, setEnquiry] = useState("any");
  const [followUp, setFollowUp] = useState([]);
  const [sort, setSort] = useState({ key: "enquiry_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);

  const updateLead = (id, patch) => setLeads((all) => all.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const addLead = (form) => {
    const now = new Date().toISOString();
    const lead = {
      id: `L${10234 + leads.length}`,
      name: form.name.trim(),
      phone: form.phone,
      email: "",
      city: "Guwahati",
      source: form.source,
      campaign: "",
      service: form.service,
      status: "new",
      enquiry_at: now,
      follow_up_at: form.follow_up_at ? moment(form.follow_up_at).toISOString() : null,
      last_note: form.note || "Added manually",
      lost_reason: "",
      activity: [{ at: now, by: "You", text: `Lead added manually (${SOURCE_BY_KEY[form.source].label})${form.note ? ` — ${form.note}` : ""}` }],
    };
    setLeads((all) => [lead, ...all]);
    toast.success(`${lead.name} added.`);
  };

  // Everything except the status tab — so the tab counts reflect the other filters.
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const enquiryFrom = { today: moment().startOf("day"), "7d": moment().subtract(6, "days").startOf("day"), "30d": moment().subtract(29, "days").startOf("day") }[enquiry];
    return leads.filter((l) => {
      if (q && !`${l.name} ${l.phone} ${l.last_note} ${l.id}`.toLowerCase().includes(q)) return false;
      if (sources.length && !sources.includes(l.source)) return false;
      if (services.length && !services.includes(l.service)) return false;
      if (enquiryFrom && moment(l.enquiry_at).isBefore(enquiryFrom)) return false;
      if (followUp.length) {
        const state = followUpState(l);
        const f = l.follow_up_at && moment(l.follow_up_at);
        const ok = followUp.some((v) =>
          v === "overdue" ? state === "overdue"
          : v === "today" ? state === "today"
          : v === "tomorrow" ? state && f.isSame(moment().add(1, "day"), "day")
          : v === "week" ? state && f.isBetween(moment().startOf("day"), moment().add(7, "days").endOf("day"))
          : v === "none" ? !state
          : false
        );
        if (!ok) return false;
      }
      return true;
    });
  }, [leads, search, sources, services, enquiry, followUp]);

  const filtered = useMemo(() => {
    const rows = status === "all" ? baseFiltered : baseFiltered.filter((l) => l.status === status);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key] || (sort.dir === "asc" ? "9999" : "0000");
      const bv = b[sort.key] || (sort.dir === "asc" ? "9999" : "0000");
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [baseFiltered, status, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const stats = useMemo(() => {
    const today = leads.filter((l) => moment(l.enquiry_at).isSame(moment(), "day")).length;
    const due = leads.filter((l) => ["overdue", "today"].includes(followUpState(l)));
    const overdue = due.filter((l) => followUpState(l) === "overdue").length;
    const month = leads.filter((l) => moment(l.enquiry_at).isAfter(moment().subtract(30, "days")));
    const won = month.filter((l) => ["converted", "booked"].includes(l.status)).length;
    return { today, due: due.length, overdue, won, rate: month.length ? Math.round((won / month.length) * 100) : 0 };
  }, [leads]);

  const sourceCounts = useMemo(
    () => Object.fromEntries(LEAD_SOURCES.map((s) => [s.key, leads.filter((l) => l.source === s.key).length])),
    [leads]
  );

  const hasFilters = search || sources.length || services.length || enquiry !== "any" || followUp.length;
  const clearFilters = () => {
    setSearch(""); setSources([]); setServices([]); setEnquiry("any"); setFollowUp([]); setPage(1);
  };
  const withReset = (setter) => (v) => { setter(v); setPage(1); };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "follow_up_at" ? "asc" : "desc" }));

  const exportLeads = () => {
    downloadXlsx(
      `leads_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
      ["Lead ID", "Name", "Phone", "Email", "City", "Source", "Campaign", "Interested in", "Status", "Enquiry", "Next follow-up", "Last note"],
      filtered.map((l) => [
        l.id, l.name, l.phone, l.email, l.city, SOURCE_BY_KEY[l.source].label, l.campaign, l.service,
        STATUS_BY_KEY[l.status].label, moment(l.enquiry_at).format("D MMM YYYY h:mm A"),
        l.follow_up_at ? moment(l.follow_up_at).format("D MMM YYYY h:mm A") : "", l.last_note,
      ]),
      { sheetName: "Leads", widths: [10, 22, 13, 26, 12, 14, 26, 18, 18, 16, 20, 20, 40] }
    );
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"}.`);
  };

  const openLead = leads.find((l) => l.id === openId);

  const SortHead = ({ k, children }) => (
    <button type="button" onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground">
      {children}
      <ArrowUpDown className={cn("h-3 w-3", sort.key === k ? "opacity-100" : "opacity-40")} />
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            Leads <Badge variant="outline" className="font-sans text-[10px] font-medium uppercase tracking-wide">Demo data</Badge>
          </h2>
          <p className="text-sm text-muted-foreground">Every enquiry from ads, listings, the website and the front desk — in one place</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportLeads}>
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> Add lead
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Users} label="Total leads" value={leads.length} hint={`${leads.filter((l) => OPEN.includes(l.status)).length} still open`} />
        <StatCard icon={Sparkles} label="New today" value={stats.today} hint="Enquiries received today" tone="text-info" />
        <StatCard
          icon={AlarmClock}
          label="Follow-ups due"
          value={stats.due}
          hint={stats.overdue ? `${stats.overdue} overdue` : "None overdue"}
          tone={stats.overdue ? "text-destructive" : undefined}
        />
        <StatCard icon={TrendingUp} label="Booked / converted (30d)" value={stats.won} hint={`${stats.rate}% conversion`} tone="text-green-600" />
      </div>

      <div className="flex flex-wrap gap-2">
        {LEAD_SOURCES.map((s) => {
          const active = sources.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => withReset(setSources)(active ? sources.filter((x) => x !== s.key) : [...sources, s.key])}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
                active ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:bg-muted/50"
              )}
            >
              <SourceMark source={s.key} />
              {s.label}
              <span className="tabular-nums text-muted-foreground">{sourceCounts[s.key]}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => withReset(setSearch)(e.target.value)}
              placeholder="Search name, phone, note or lead ID…"
              className="h-9 pl-9"
            />
          </div>
          <FilterMultiSelect label="Source" options={LEAD_SOURCES.map((s) => ({ value: s.key, label: s.label }))} value={sources} onChange={withReset(setSources)} />
          <FilterMultiSelect label="Interested in" options={LEAD_SERVICES.map((s) => ({ value: s, label: s }))} value={services} onChange={withReset(setServices)} />
          <FilterMultiSelect label="Follow-up" options={FOLLOW_UP_OPTIONS} value={followUp} onChange={withReset(setFollowUp)} />
          <SegmentedControl size="sm" items={ENQUIRY_PRESETS} value={enquiry} onChange={withReset(setEnquiry)} />
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          ) : null}
        </div>
        <SegmentedControl
          items={[
            { value: "all", label: "All", count: baseFiltered.length },
            ...LEAD_STATUSES.map((s) => ({ value: s.key, label: s.label, count: baseFiltered.filter((l) => l.status === s.key).length })),
          ]}
          value={status}
          onChange={withReset(setStatus)}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Lead</th>
                <th className="px-4 py-3 font-medium">Interested in</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"><SortHead k="enquiry_at">Enquiry</SortHead></th>
                <th className="px-4 py-3 font-medium"><SortHead k="follow_up_at">Next follow-up</SortHead></th>
                <th className="px-4 py-3 font-medium">Last note</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                    No leads match these filters.
                    {hasFilters ? <Button variant="link" onClick={clearFilters}>Clear filters</Button> : null}
                  </td>
                </tr>
              ) : (
                rows.map((l) => (
                  <tr key={l.id} onClick={() => setOpenId(l.id)} className="cursor-pointer border-t border-border hover:bg-muted/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <SourceMark source={l.source} />
                        <div className="min-w-0">
                          <p className="truncate font-medium">{l.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {l.phone}
                            {l.campaign && <span> · {l.campaign}</span>}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{l.service}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground" title={moment(l.enquiry_at).format("D MMM YYYY, h:mm A")}>
                      {moment(l.enquiry_at).fromNow()}
                    </td>
                    <td className="px-4 py-3"><FollowUpCell lead={l} /></td>
                    <td className="max-w-[240px] px-4 py-3">
                      <p className="line-clamp-2 text-muted-foreground">{l.last_note}</p>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Call ${l.name}`} onClick={() => { window.location.href = `tel:${l.phone}`; }}>
                          <Phone className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`WhatsApp ${l.name}`} onClick={() => window.open(whatsappUrl(l), "_blank")}>
                          <MessageCircle className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-sm text-muted-foreground">
          <span>
            {filtered.length === 0 ? "0" : `${(current - 1) * PAGE_SIZE + 1}–${Math.min(current * PAGE_SIZE, filtered.length)}`} of {filtered.length} leads
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={current <= 1} onClick={() => setPage(current - 1)} aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>Page {current} of {pages}</span>
            <Button size="sm" variant="outline" disabled={current >= pages} onClick={() => setPage(current + 1)} aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <LeadDrawer key={openId} lead={openLead} onClose={() => setOpenId(null)} onUpdate={updateLead} />
      <AddLeadModal open={adding} onOpenChange={setAdding} onAdd={addLead} />
    </div>
  );
}
